/**
 * HTTP Service
 * 
 * Provides HTTP-like methods (get, post, patch, delete) that map to Supabase operations.
 * This service acts as an adapter between components expecting HTTP methods and the Supabase client.
 */

import { createClient } from './supabase';

// Type definitions for HTTP requests
export interface RequestConfig {
  params?: Record<string, any>;
  headers?: Record<string, string>;
  body?: any;
}

export interface ResponseData<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers?: Record<string, string>;
}

class HttpService {
  /**
   * Performs a GET request
   * @param url The endpoint URL
   * @param config Optional request configuration
   * @returns Promise with response data
   */
  async get<T = any>(url: string, config?: RequestConfig): Promise<ResponseData<T>> {
    try {
      const supabase = await createClient();
      const { params } = config || {};
      
      // Extract table name from URL path
      const tableName = this.getTableNameFromUrl(url);
      
      // Determine if we're fetching a collection or a single item
      const isCollection = !url.match(/\/[a-zA-Z0-9-_]+$/);
      
      let query: any = supabase.from(tableName).select('*');
      
      // Apply filters from params
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (key === 'limit') {
            query = query.limit(value as number);
          } else if (key === 'sort' && params.order) {
            query = query.order(value as string, { ascending: params.order === 'asc' });
          } else if (key !== 'order') { // Skip 'order' as it's handled with 'sort'
            query = query.eq(key, value);
          }
        });
      }
      
      // If URL has an ID at the end, fetch a single item
      if (!isCollection) {
        const id = this.getIdFromUrl(url);
        query = query.eq('id', id).single();
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return {
        data: data as T,
        status: 200,
        statusText: 'OK'
      };
    } catch (error) {
      console.error('GET request failed:', error);
      throw error;
    }
  }

  /**
   * Performs a POST request
   * @param url The endpoint URL
   * @param data The data to send
   * @param config Optional request configuration
   * @returns Promise with response data
   */
  async post<T = any>(url: string, data?: any, _config?: RequestConfig): Promise<ResponseData<T>> {
    try {
      const supabase = await createClient();
      
      // Extract table name from URL path
      const tableName = this.getTableNameFromUrl(url);
      
      const { data: responseData, error } = await supabase
        .from(tableName)
        .insert(data)
        .select()
        .single();
      
      if (error) throw error;
      
      return {
        data: responseData as T,
        status: 201,
        statusText: 'Created'
      };
    } catch (error) {
      console.error('POST request failed:', error);
      throw error;
    }
  }

  /**
   * Performs a PATCH request
   * @param url The endpoint URL
   * @param data The data to update
   * @param config Optional request configuration
   * @returns Promise with response data
   */
  async patch<T = any>(url: string, data?: any, _config?: RequestConfig): Promise<ResponseData<T>> {
    try {
      const supabase = await createClient();
      
      // Extract table name and ID from URL path
      const tableName = this.getTableNameFromUrl(url);
      const id = this.getIdFromUrl(url);
      
      const { data: responseData, error } = await supabase
        .from(tableName)
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return {
        data: responseData as T,
        status: 200,
        statusText: 'OK'
      };
    } catch (error) {
      console.error('PATCH request failed:', error);
      throw error;
    }
  }

  /**
   * Performs a DELETE request
   * @param url The endpoint URL
   * @param config Optional request configuration
   * @returns Promise with response data
   */
  async delete<T = any>(url: string, _config?: RequestConfig): Promise<ResponseData<T>> {
    try {
      const supabase = await createClient();
      
      // Extract table name and ID from URL path
      const tableName = this.getTableNameFromUrl(url);
      const id = this.getIdFromUrl(url);
      
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return {
        data: {} as T,
        status: 204,
        statusText: 'No Content'
      };
    } catch (error) {
      console.error('DELETE request failed:', error);
      throw error;
    }
  }

  /**
   * Helper method to extract table name from URL
   * @param url The URL path
   * @returns The table name
   */
  private getTableNameFromUrl(url: string): string {
    // Remove leading slash and query parameters
    const cleanUrl = url.replace(/^\//, '').split('?')[0];
    
    // Map URL paths to table names
    const urlToTableMap: Record<string, string> = {
      'charity/donations': 'donations',
      'donations': 'donations',
      'events': 'events',
      'products': 'products',
      'staff': 'business_staff',
      'volunteers': 'volunteers',
      'api/auth/sync-auth-users': 'auth_sync',
      'auth/invitation': 'business_staff_invitations'
    };
    
    // Find the matching table name
    for (const [urlPath, tableName] of Object.entries(urlToTableMap)) {
      if (cleanUrl.startsWith(urlPath)) {
        return tableName;
      }
    }
    
    // Default to the last segment of the URL as the table name
    return cleanUrl.split('/').pop() || 'unknown';
  }

  /**
   * Helper method to extract ID from URL
   * @param url The URL path
   * @returns The ID from the URL
   */
  private getIdFromUrl(url: string): string {
    // Extract the last segment of the URL as the ID
    const segments = url.replace(/\/$/, '').split('/');
    return segments[segments.length - 1];
  }
}

export const httpService = new HttpService();
